import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";

const iosSwitchStyles = `
.ios-switch {
  position: relative;
  display: inline-block;
  width: 42px;
  height: 24px;
  vertical-align: middle;
}
.ios-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.ios-slider {
  position: absolute;
  cursor: pointer;
  top: 0; left: 0; right: 0; bottom: 0;
  background-color: #e4e4e7;
  border-radius: 24px;
  transition: .4s;
}
.ios-slider:before {
  position: absolute;
  content: "";
  height: 20px;
  width: 20px;
  left: 2px;
  bottom: 2px;
  background-color: white;
  border-radius: 50%;
  transition: .4s;
  box-shadow: 0 2px 6px rgba(0,0,0,0.15);
}
.ios-switch input:checked + .ios-slider {
  background-color: #2563eb;
}
.ios-switch input:checked + .ios-slider:before {
  transform: translateX(18px);
}
.ios-switch input:disabled + .ios-slider {
  background-color: #2563eb;
  cursor: not-allowed;
  opacity: 0.7;
}
`;

const responsiveModalStyles = `
.cookie-modal-content {
  border-radius: 16px;
  padding: 32px 24px 24px 24px;
  max-width: 1000px;
  width: 100%;
  background: #fff;
  position: relative;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
}

@media (max-width: 768px) {
  .cookie-modal-content {
    margin: 16px;
    padding: 24px 16px 16px 16px;
  }
  .cookie-modal-content h4 {
    font-size: 1.15rem;
  }
  .cookie-modal-content p, 
  .cookie-modal-content strong,
  .cookie-modal-content .cookie-description {
    font-size: 0.9em;
  }
  .cookie-preference-item {
    flex-direction: column;
    align-items: flex-start;
  }
  .cookie-preference-item .ios-switch {
    margin-top: 12px;
  }
}
`;

const CookiePreferenceModal = ({ show, onClose, onSave }) => {
  const [analytics, setAnalytics] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    if (show) {
      const savedPrefs = localStorage.getItem('cookiePrefs');
      if (savedPrefs) {
        const prefs = JSON.parse(savedPrefs);
        setAnalytics(prefs.analytics || false);
      }
    }
  }, [show]);

  const removeGoogleAnalyticsCookies = () => {
    // Get all existing cookies
    const allCookies = document.cookie.split(';');
    
    // Patterns to match Google Analytics cookies
    const cookiePatterns = [
      /^_ga($|_)/,           // Matches _ga, _ga_, _ga_xxxxx
      /^_gid($|_)/,          // Matches _gid, _gid_xxxxx  
      /^_gat($|_)/,          // Matches _gat, _gat_xxxxx
      /^_gtag_/,             // Matches _gtag_xxxxx
      /^_gcl_au/,            // Matches _gcl_au
      /^AMP_TOKEN/,          // AMP related
      /^_dc_gtm_/            // GTM related
    ];

    // Remove cookies that match the patterns
    allCookies.forEach(cookie => {
      const cookieName = cookie.split('=')[0].trim();
      
      // Check if cookie name matches any GA pattern
      const isGACookie = cookiePatterns.some(pattern => pattern.test(cookieName));
      
      if (isGACookie) {
        // Remove from current domain
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
        // Remove from current domain with leading dot
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname};`;
        // Remove from parent domain
        const domainParts = window.location.hostname.split('.');
        if (domainParts.length > 2) {
          const parentDomain = '.' + domainParts.slice(-2).join('.');
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${parentDomain};`;
        }
        console.log(`Removed cookie: ${cookieName}`);
      }
    });

    // Also specifically target your cookie name
    const specificCookies = ['_ga_4N2GBHW9RF'];
    specificCookies.forEach(cookieName => {
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${window.location.hostname};`;
      const domainParts = window.location.hostname.split('.');
      if (domainParts.length > 2) {
        const parentDomain = '.' + domainParts.slice(-2).join('.');
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${parentDomain};`;
      }
      console.log(`Specifically removed cookie: ${cookieName}`);
    });

    // Clear Google Analytics from localStorage and sessionStorage
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('_ga') || key.startsWith('gtag') || key.includes('google') || key.includes('analytics')) {
        // Don't remove cookiePrefs
        if (key !== 'cookiePrefs') {
          localStorage.removeItem(key);
          console.log(`Removed from localStorage: ${key}`);
        }
      }
    });

    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith('_ga') || key.startsWith('gtag') || key.includes('google') || key.includes('analytics')) {
        sessionStorage.removeItem(key);
        console.log(`Removed from sessionStorage: ${key}`);
      }
    });

    // Set global disable flag
    window['ga-disable-GTM-N5NF8XNS'] = true;
    
    console.log('All Google Analytics cookies removed, including _ga_4N2GBHW9RF');
  };

  const handleSave = () => {
    const preferences = { analytics };
    const currentPrefs = localStorage.getItem('cookiePrefs');
    const wasAnalyticsEnabled = currentPrefs ? JSON.parse(currentPrefs).analytics : true;
    
    // Save preferences first
    onSave(preferences);
    
    if (!analytics) {
      // If analytics is disabled, remove existing cookies and tracking
      removeGoogleAnalyticsCookies();
      console.log('Analytics tracking disabled and cookies removed');
      onClose();
    } else if (analytics && !wasAnalyticsEnabled) {
      // If analytics was disabled and now enabled, reload page to reinitialize
      console.log('Analytics enabled - reloading page to reinitialize tracking');
      onClose();
      // Small delay to ensure modal closes and preferences are saved before reload
      setTimeout(() => {
        window.location.reload();
      }, 300);
    } else {
      // Analytics was already enabled, just close
      window['ga-disable-GTM-N5NF8XNS'] = false;
      console.log('Analytics tracking confirmed as enabled');
      onClose();
    }
  };

  if (!show) return null;

  return (
    <>
      <style>{iosSwitchStyles}</style>
      <style>{responsiveModalStyles}</style>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1050,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        }}
      >
        <div className="cookie-modal-content">
          <button
            type="button"
            className="close"
            onClick={onClose}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              fontSize: '1.5rem',
              lineHeight: '1',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#666',
              width: '30px',
              height: '30px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background-color 0.2s',
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#f5f5f5'}
            onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            <span>&times;</span>
          </button>
          <h4 className="modal-title" style={{ 
            fontWeight: 600, 
            marginBottom: '0.75em',
            color: '#333',
            fontSize: '1.25rem'
          }}>
              {t("modal.title")}
          </h4>
          <p style={{ 
            fontSize: '1em', 
            marginBottom: '1.5em', 
            color: '#666',
            lineHeight: '1.5'
          }}>
              {t("modal.intro")}
          </p>
          <div style={{ 
            marginBottom: '1.5em', 
            borderBottom: '1px solid #eee', 
            paddingBottom: '1em' 
          }}>
            <div className="cookie-preference-item" style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center' 
            }}>
              <div>
                <strong style={{ color: '#333', fontSize: '1rem' }}>
                 {t("modal.necessaryTitle")}
                </strong>
                <div className="cookie-description" style={{ 
                  fontSize: '0.9em', 
                  color: '#666',
                  marginTop: '4px',
                  lineHeight: '1.4'
                }}>
                    {t("modal.necessaryDesc")}
                </div>
              </div>
              <label className="ios-switch">
                <input type="checkbox" checked disabled />
                <span className="ios-slider"></span>
              </label>
            </div>
          </div>
          <div style={{ 
            marginBottom: '1.5em', 
            borderBottom: '1px solid #eee', 
            paddingBottom: '1em' 
          }}>
            <div className="cookie-preference-item" style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center' 
            }}>
              <div>
                <strong style={{ color: '#333', fontSize: '1rem' }}>
                  {t("modal.analyticsTitle")}
                </strong>
                <div className="cookie-description" style={{ 
                  fontSize: '0.9em', 
                  color: '#666',
                  marginTop: '4px',
                  lineHeight: '1.4'
                }}>
                    {t("modal.analyticsDesc")}
                </div>
              </div>
              <label className="ios-switch">
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={() => setAnalytics(!analytics)}
                />
                <span className="ios-slider"></span>
              </label>
            </div>
          </div>
          <button 
            className="btn btn-primary w-100" 
            style={{ 
              borderRadius: '8px', 
              fontSize: '1.1em',
              padding: '12px 24px',
              border: 'none',
              backgroundColor: '#2563eb',
              color: 'white',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'background-color 0.2s',
              width: '100%'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#1d4ed8'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#2563eb'}
            onClick={handleSave}
          >
            {t("modal.saveButton")}
          </button>
        </div>
      </div>
    </>
  );
};

export default CookiePreferenceModal;